import { CareMapState, MYSELF } from '../../../caremap/CareMapState';
import { IContactMethod, IHouseholdMember, IPatient } from '../../../caremap/ICareMap';
import { LabelProvider } from '../../../config/LabelProvider';
import { HistoryViewModel } from '../../../support/HistoryViewModel';
import { UserState } from '../../../user/UserState';
import { QuestionHelper } from '../core/QuestionHelper';
import { IMiniWizardConfig } from '../core/IMiniWizardConfig';
import { IMiniWizardProvider } from '../core/IMiniWizardProvider';
import { ICheckboxQuestion } from '../question-types/CheckboxQuestionProvider';
import { INameQuestion } from '../question-types/NameQuestionProvider';
import { IYesNoQuestion } from '../question-types/YesNoQuestionProvider';
import { IQuestion } from '../core/IQuestion';
import { MixPanelEventEmitter } from '../../../metrics/MixPanelEventEmitter';
import { OnboardCareRecipientWizard } from './OnboardCareRecipientWizard';
import { deepClone } from '../../../utils/deepClone';

export class NewCareMapWizard implements IMiniWizardProvider {
    static inject = () => [
        CareMapState,
        LabelProvider,
        UserState,
        HistoryViewModel,
        MixPanelEventEmitter,
        OnboardCareRecipientWizard
    ];

    constructor(
        private careMapState: CareMapState,
        private labelProvider: LabelProvider,
        private userState: UserState,
        private history: HistoryViewModel,
        private mixpanel: MixPanelEventEmitter,
        private phase2Wizard: OnboardCareRecipientWizard
    ) {}

    wizardKey = 'new-care-recipient';

    private includeConsent = true;

    // can pass in a param that should be a route
    private advanceTo: string | undefined;

    private questions: {
        'recipient-self': IYesNoQuestion,
        'consent-self': ICheckboxQuestion,
        'consent-someone-else': ICheckboxQuestion,
        'my-name': INameQuestion,
        'care-recipient-name': INameQuestion,
    } = {

            'recipient-self': {
                questionId: 'recipient-self',
                questionType: 'yes-no',
                questionTitle: 'Are you managing care for yourself or someone else, like your child or parent?',
                subTitle: 'Are you the Care Recipient, or is someone else?',
                labels: {
                    yes: 'Myself',
                    no: 'Someone Else'
                },
                autoAdvance: true,
                trackingHandler: (answer: any) => {
                    this.mixpanel.track('Care Mapping for Myself or Someone Else Step Completed', () => ({
                        Selection: answer ? 'myself': 'someone else'
                    }));
                }
            },
            'consent-self': {
                questionId: 'consent-self',
                questionType: 'checkbox',
                questionTitle: `We need your permission to continue:`,
                checkboxLabel: '',
                autoAdvance: true,
                trackingHandler: (answer: any) => {
                    this.mixpanel.track('Data Consent Step Completed', () => {
                        return {
                            'Care Recipient Is': 'myself'
                        };
                    });
                }
            },
            'consent-someone-else': {
                questionId: 'consent-someone-else',
                questionType: 'checkbox',
                questionTitle: `We need your permission to continue:`,
                checkboxLabel: '',
                autoAdvance: true,
                trackingHandler: (answer: any) => {
                    this.mixpanel.track('Data Consent Step Completed', () => {
                        return {
                            'Care Recipient Is': 'someone-else'
                        };
                    });
                }
            },
            'care-recipient-name': {
                questionId: 'care-recipient-name',
                questionType: 'name',
                questionTitle: `Who is the care recipient?`,
            },
            'my-name': {
                questionId: 'my-name',
                questionType: 'name',
                questionTitle: 'What is your name?',
                trackingHandler: (answer: any) => {
                    this.mixpanel.track('User Name Step Completed', () => {
                        const fieldsUsed = [
                            answer.firstName ? 'first' : '',
                            answer.lastName ? 'last' : '',
                        ].filter(field => Boolean(field));

                        return {
                            'Data Entered': fieldsUsed
                        };
                    });
                }
            }
        };

    private buildQuestions = () => {
        const quests = [];

        quests.push(
            this.questions['my-name'],
            this.questions['recipient-self']
        );

        if (this.includeConsent) {
            quests.push(
                this.questions['consent-self'],
                this.questions['consent-someone-else']
            );
        }

        quests.push(this.questions['care-recipient-name']);

        const questions: IQuestion[] = deepClone(quests);
        const helper = new QuestionHelper(questions);
        const activeCareMap = this.careMapState.getActiveCaremap();

        if (this.userState.state.firstName){
            helper.getQuestion(this.questions['my-name']).disabled = true;
        }

        if (this.careMapState.hasAnyCareMapForSelf()) {
            // disable recipient self, we know the answer is someone else
            const recipientSelf = helper.getQuestion(this.questions['recipient-self']);
            recipientSelf.answer = false;
            recipientSelf.disabled = true;

            helper.getQuestion(this.questions['consent-self']).disabled = true;

            this.applySomeoneElseLabels(helper);
        } else if (activeCareMap && !activeCareMap.essentialMapComplete) {
            // set the answer if there's an active caremap and it was autoCreated
            helper.getQuestion(this.questions['recipient-self']).answer = this.careMapState.isUserThePatient();
        }

        return questions;
    };

    create = (params: Map<string, string>): IMiniWizardConfig => {
        this.includeConsent = !Boolean(params.get('skip-consent'));
        this.advanceTo = params.get('advance-to') || 'phase2';

        // if there's an autoCreated caremap
        const autoCreatedCareMap = this.careMapState.getIncompleteCareMap();
        if (autoCreatedCareMap) {
            this.careMapState.setActiveCaremap(autoCreatedCareMap.careMapId);
        } else {
            // clear active caremap
            this.careMapState.unsetCurrentCareMap();
        }

        return {
            title: 'New Care Recipient',
            questions: this.buildQuestions(),
            doneLabel: 'Next',
            allowAutoAdvanceOnLastStep: true,
            onComplete: async (answeredQuestions) => {
                const answerHelper = new QuestionHelper(answeredQuestions);

                const isRecipientSelf = answerHelper.getAnswer(this.questions['recipient-self'])!;

                let patient: Partial<IPatient> = {};

                if (answerHelper.getAnswer(this.questions['my-name'])){
                    const { firstName, lastName } = answerHelper.getAnswer(this.questions['my-name'])!;
                    this.userState.setFirstName(firstName);
                    if (lastName){
                        this.userState.setLastName(lastName);
                    }
                    await this.userState.save();
                }

                if (isRecipientSelf) {
                    const { firstName, lastName, email, address, phone } = this.userState.state;
                    const contactMethods: IContactMethod[] = [{type: 'email', value: email!}];
                    if (phone) {
                        const formatted = /^\d{10}$/.test(phone) ? `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}` : phone;
                        contactMethods.push({type: 'mobile-phone', value: formatted});
                    }
                    patient = {
                        ...patient,
                        firstName,
                        lastName,
                        address,
                        contactMethods
                    };
                } else {
                    const { firstName, lastName } = answerHelper.getAnswer(this.questions['care-recipient-name'])!;
                    patient = {
                        ...patient,
                        firstName,
                        lastName
                    };
                }

                // there's a caremap already, (ie appt recording), so simply update instead of create new
                let careMapId: string;
                const existingCareMap = this.careMapState.getActiveCaremap();

                if (existingCareMap) {
                    this.careMapState.updatePatient({
                        ...existingCareMap.patient,
                        ...patient
                    }, {
                        markEssentialMapComplete: true,
                        initialWizardAnswers: [{
                            questionId: 'recipient-self',
                            answer: isRecipientSelf
                        },{
                            questionId: 'recipient',
                            answer: isRecipientSelf ? MYSELF : undefined
                        }]
                    });

                    careMapId = existingCareMap.careMapId!;
                } else {
                    careMapId = (await this.createCaremap({ patient, isRecipientSelf })).careMapId;
                }

                // replace with care so that back goes to this caremap, not to the new-care-recipient wizard
                this.history.replace(`/care/${careMapId}`);

                if (this.advanceTo === 'phase2') {
                    // but actually go to phase 2 of the wizard
                    this.phase2Wizard.start(careMapId);
                } else if (this.advanceTo) {
                    this.history.push(this.advanceTo);
                }
            },

            onAnswered: (answeredQuestion, allQuestions) => {
                const answerHelper = new QuestionHelper(allQuestions);

                if (answerHelper.isSameQuestionId(answeredQuestion, this.questions['recipient-self'])) {
                    this.onRecipientSelfAnswered(answerHelper);
                }

                return allQuestions;
            }
        };
    };

    start = (urlParams?: any, options?: { trackingFrom?: 'type-method-choice' | 'complete-care-profile' | 'type-it-instead' }) => {
        const search = new URLSearchParams(urlParams).toString();
        this.history.push(`/q/${this.wizardKey}${search ? `?${search}` : ''}`);
        this.mixpanel.track('Add Care Recipient Questionnaire Started', () => ({
            'Started From': options?.trackingFrom ?? 'unknown'
        }));
    };

    private createCaremap = async ({
        patient,
        isRecipientSelf,
        householdMembers
    }: {
        patient: Partial<IPatient>;
        isRecipientSelf: boolean;
        householdMembers?: IHouseholdMember[];
    }) => {
        const caremap = await this.careMapState.addNewCaremapV3({ patient, isRecipientSelf, householdMembers });
        return caremap;
    };

    private onRecipientSelfAnswered = (answerHelper: QuestionHelper) => {
        const recipientIsSelf = answerHelper.getAnswer(this.questions['recipient-self']);

        if (this.includeConsent) {
            if (recipientIsSelf === true) {
                answerHelper.getQuestion(this.questions['consent-someone-else']).disabled = true;
                answerHelper.getQuestion(this.questions['care-recipient-name']).disabled = true;
                answerHelper.getQuestion(this.questions['consent-self']).disabled = false;

                this.applySelfLabels(answerHelper);

            }
            if (recipientIsSelf === false) {
                answerHelper.getQuestion(this.questions['consent-someone-else']).disabled = false;
                answerHelper.getQuestion(this.questions['care-recipient-name']).disabled = false;
                answerHelper.getQuestion(this.questions['consent-self']).disabled = true;

                this.applySomeoneElseLabels(answerHelper);
            }
        }
    };

    private applySelfLabels = (answerHelper: QuestionHelper) => {
        this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable = true;
        answerHelper.getQuestion(this.questions['consent-self'])
            .checkboxLabel = this.labelProvider.getLabel('CONSENT');
    };

    private applySomeoneElseLabels = (answerHelper: QuestionHelper) => {
        this.labelProvider.use_MY_CAREMAP__PrefixIfApplicable = false;

        answerHelper.getQuestion(this.questions['consent-someone-else'])
            .checkboxLabel = this.labelProvider.getLabel('CONSENT');

    };
};
